import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { RequirementWithDetails } from '../services/requirementService';

interface DiscussionHotspotsCardProps {
  requirements: RequirementWithDetails[];
  projectId: string;
}

interface RequirementWithActivity extends RequirementWithDetails {
  totalDiscussions: number;
}

export function DiscussionHotspotsCard({ requirements, projectId }: DiscussionHotspotsCardProps): React.ReactElement {
  const navigate = useNavigate();

  // Calculate discussion activity for each requirement
  const requirementsWithActivity: RequirementWithActivity[] = requirements
    .map(req => ({
      ...req,
      totalDiscussions: (req.comment_count || 0) + (req.reply_count || 0)
    }))
    .filter(req => req.totalDiscussions > 0) // Only show requirements with discussions
    .sort((a, b) => b.totalDiscussions - a.totalDiscussions) // Sort by most discussions
    .slice(0, 5); // Show top 5

  // Get priority color
  const getPriorityColor = (priority: string): string => {
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-green-100 text-green-800 border-green-300'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Get status color
  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      approved: 'bg-green-100 text-green-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      completed: 'bg-purple-100 text-purple-800',
      rejected: 'bg-red-100 text-red-800',
      deferred: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get discussion intensity color
  const getDiscussionIntensity = (count: number): { color: string; label: string } => {
    if (count >= 20) return { color: 'text-red-600 bg-red-50', label: 'Very High' };
    if (count >= 10) return { color: 'text-orange-600 bg-orange-50', label: 'High' };
    if (count >= 5) return { color: 'text-yellow-600 bg-yellow-50', label: 'Moderate' };
    return { color: 'text-blue-600 bg-blue-50', label: 'Low' };
  };

  // Format time ago
  const formatTimeAgo = (dateString: string | null): string => {
    if (!dateString) return 'No activity';

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleRequirementClick = (requirementId: string) => {
    navigate(`/projects/${projectId}/requirements/${requirementId}`);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Discussion Hotspots</h3>
        <svg
          className="w-5 h-5 text-orange-500"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Requirements with the most discussion activity (comments + replies)
      </p>

      {requirementsWithActivity.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-500">No discussions yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requirementsWithActivity.map(req => {
            const intensity = getDiscussionIntensity(req.totalDiscussions);

            return (
              <div
                key={req.id}
                onClick={() => handleRequirementClick(req.id)}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-indigo-300 cursor-pointer transition-all"
              >
                {/* Header with priority and status */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getPriorityColor(req.priority)}`}>
                      {req.priority}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(req.status)}`}>
                      {req.status}
                    </span>
                  </div>

                  {/* Discussion count badge */}
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${intensity.color} border border-current`}>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm font-semibold">{req.totalDiscussions}</span>
                  </div>
                </div>

                {/* Requirement title */}
                <h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                  {req.title}
                </h4>

                {/* Metadata */}
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    {req.comment_count || 0} comments
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    {req.reply_count || 0} replies
                  </span>
                  {req.latest_comment_date && (
                    <span className="inline-flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {formatTimeAgo(req.latest_comment_date)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Discussion Intensity Legend */}
      {requirementsWithActivity.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-700 mb-2">Discussion Intensity:</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="inline-flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-gray-600">Low (1-4)</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-gray-600">Moderate (5-9)</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span className="text-gray-600">High (10-19)</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-gray-600">Very High (20+)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
